import { Component, OnInit, inject, signal, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
  CrmService,
  ContactoDto,
  FichaContacto,
  CitaClinica,
  EmbudoDto,
  CasoDto,
  ActividadDto,
} from '../core/crm.service';

@Component({
  selector: 'app-pacientes',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <div class="exp-layout">
      <!-- Directorio lateral de pacientes -->
      <aside class="card exp-sidebar">
        <div class="exp-side-top">
          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px;">
            <b style="font-size: 14px;">Directorio ({{ contactos().length }})</b>
            <button type="button" class="btn pri" style="height: 30px; padding: 0 11px; font-size: 11px;" (click)="crm.modalNuevoPaciente.set(true)">
              + Paciente
            </button>
          </div>
          <input
            type="search"
            class="modal-input"
            style="width: 100%; height: 34px; font-size: 12px;"
            placeholder="Filtrar por documento, nombre o tel..."
            [(ngModel)]="terminoBusqueda"
            (input)="buscar()"
          />
        </div>

        <div class="exp-list">
          @for (p of contactos(); track p.id) {
            <div
              class="exp-item"
              [class.active]="ficha()?.contacto?.id === p.id"
              (click)="seleccionarPaciente(p.id)"
            >
              <div class="exp-avatar">{{ iniciales(p.nombres + ' ' + (p.apellidos || '')) }}</div>
              <div style="min-width: 0; flex: 1;">
                <div style="font-weight: 700; font-size: 12.5px; color: var(--ink);">
                  {{ p.nombres }} {{ p.apellidos }}
                </div>
                <div class="m">
                  {{ p.tipoDocumento || 'CC' }} {{ p.numeroDocumento || 'Sin doc' }} · {{ p.telefono || 'Sin tel' }}
                </div>
              </div>
            </div>
          }
        </div>
      </aside>

      <!-- Expediente 360° del Paciente -->
      <section class="exp-main">
        @if (ficha(); as f) {
          <!-- Hero Card del Paciente -->
          <div class="card hero-patient">
            <div class="hero-top">
              <div style="display: flex; align-items: center; gap: 14px;">
                <div class="hero-avatar">{{ iniciales(f.contacto.nombres + ' ' + (f.contacto.apellidos || '')) }}</div>
                <div>
                  <div class="eyebrow-tag">EXPEDIENTE ÚNICO POR DOCUMENTO</div>
                  <h2 style="margin: 2px 0 0; font-size: 22px; font-weight: 800; color: var(--ink);">
                    {{ f.contacto.nombres }} {{ f.contacto.apellidos }}
                  </h2>
                </div>
              </div>

              <button type="button" class="btn pri" (click)="abrirNuevoCasoPaciente(f.contacto.id)">
                + Nuevo caso para este paciente
              </button>
            </div>

            <div class="hero-meta-grid">
              <div class="meta-box">
                <div class="m">Documento</div>
                <b>{{ f.contacto.tipoDocumento || 'CC' }} {{ f.contacto.numeroDocumento || '—' }}</b>
              </div>
              <div class="meta-box">
                <div class="m">Teléfono / WhatsApp</div>
                <b>{{ f.contacto.telefono || '—' }}</b>
              </div>
              <div class="meta-box">
                <div class="m">Correo electrónico</div>
                <b>{{ f.contacto.correo || '—' }}</b>
              </div>
              <div class="meta-box">
                <div class="m">Canal de ingreso</div>
                <b>{{ formatearCanal(f.contacto.origen) }}</b>
              </div>
              <div class="meta-box">
                <div class="m">Habeas Data (Ley 1581)</div>
                <b [style.color]="f.contacto.consentimientoDatos ? '#15803D' : '#B45309'">
                  {{ f.contacto.consentimientoDatos ? '✓ Autorizado' : 'Pendiente' }}
                </b>
              </div>
            </div>
          </div>

          <!-- Tarjeta de Citas Clínicas del Paciente (Fuente Clínica) -->
          <div class="card" style="overflow: hidden;">
            <div class="section-bar">
              <div>
                <b>Citas y Atenciones en Sistema Clínico ({{ citas().length }})</b>
                <span class="m" style="margin-left: 8px;">Consultadas automáticamente por documento</span>
              </div>
            </div>
            @if (citas().length > 0) {
              <table>
                <thead>
                  <tr>
                    <th>Fecha y hora</th>
                    <th>Especialidad / Servicio</th>
                    <th>Profesional</th>
                    <th>Sede</th>
                    <th>Estado clínico</th>
                  </tr>
                </thead>
                <tbody>
                  @for (c of citas(); track c.fechaHora; let u = $last) {
                    <tr>
                      <td [style.border-bottom]="u ? '0' : ''"><b>{{ c.fechaHora | date:'dd MMM yyyy, h:mm a' }}</b></td>
                      <td [style.border-bottom]="u ? '0' : ''">{{ c.especialidad }}</td>
                      <td [style.border-bottom]="u ? '0' : ''">{{ c.profesional }}</td>
                      <td [style.border-bottom]="u ? '0' : ''">{{ c.sede }}</td>
                      <td [style.border-bottom]="u ? '0' : ''">
                        <span
                          class="due"
                          [style.background]="c.estado === 'atendida' ? '#DCFCE7' : (c.estado === 'programada' ? '#E8F1FB' : '#FEE2E2')"
                          [style.color]="c.estado === 'atendida' ? '#15803D' : (c.estado === 'programada' ? '#035CB1' : '#B91C1C')"
                        >
                          {{ c.estado | uppercase }}
                        </span>
                      </td>
                    </tr>
                  }
                </tbody>
              </table>
            } @else {
              <div style="padding: 16px 20px;" class="m">
                {{ avisoCitas() || 'No se registran citas en la fuente clínica para este documento.' }}
              </div>
            }
          </div>

          <!-- Casos del paciente con recorrido de etapas (#t-paciente mejorado) -->
          @for (caso of f.casos; track caso.id) {
            <div class="card" style="overflow: hidden;">
              <div class="section-bar">
                <div>
                  <b style="font-size: 14px;">{{ caso.titulo }}</b>
                  <span class="m" style="margin-left: 10px;">Proceso: {{ caso.embudo }}</span>
                </div>
                <span
                  class="due"
                  [style.background]="caso.estado === 'ganado' ? '#DCFCE7' : '#FEF3C7'"
                  [style.color]="caso.estado === 'ganado' ? '#15803D' : '#B45309'"
                >
                  {{ caso.estado === 'ganado' ? 'COMPLETADO' : (caso.estado | uppercase) }}
                </span>
              </div>

              <table>
                <thead>
                  <tr>
                    <th>Etapa del proceso</th>
                    <th>Estado</th>
                    <th>Fecha / Vencimiento</th>
                    <th>Canal</th>
                    <th style="text-align: right;">Control</th>
                  </tr>
                </thead>
                <tbody>
                  @for (et of etapasDeEmbudo(caso.embudoId); track et.id; let ult = $last) {
                    <tr [style.background]="et.id === caso.etapaId ? '#EFF6FF' : ''">
                      <td [style.border-bottom]="ult ? '0' : ''">
                        @if (et.id === caso.etapaId) {
                          <b>{{ et.orden }}. {{ et.nombre }}</b>
                        } @else {
                          <span [style.color]="estadoEtapa(et.orden, caso) === 'Pendiente' ? '#64748B' : ''">{{ et.orden }}. {{ et.nombre }}</span>
                        }
                      </td>
                      <td
                        [style.border-bottom]="ult ? '0' : ''"
                        [style.color]="estadoEtapa(et.orden, caso) === 'Completada' ? '#15803D' : (estadoEtapa(et.orden, caso) === 'Pendiente' ? '#64748B' : '#035CB1')"
                      >
                        <b>{{ estadoEtapa(et.orden, caso) }}</b>
                      </td>
                      <td [style.border-bottom]="ult ? '0' : ''">
                        @if (et.id === caso.etapaId && caso.fechaVencimiento) {
                          <b style="color: var(--amber);">Vence {{ caso.fechaVencimiento | date:'dd MMM, h:mm a' }}</b>
                        } @else if (estadoEtapa(et.orden, caso) === 'Completada') {
                          {{ caso.creadoEn | date:'dd MMM yyyy' }}
                        } @else {
                          —
                        }
                      </td>
                      <td [style.border-bottom]="ult ? '0' : ''" class="m">
                        {{ et.id === caso.etapaId ? formatearCanal(caso.origen) : '' }}
                      </td>
                      <td [style.border-bottom]="ult ? '0' : ''" style="text-align: right;">
                        @if (et.id !== caso.etapaId && caso.estado === 'abierto') {
                          <button type="button" class="move-here-btn" (click)="moverEtapa(caso, et.id)">
                            Situar aquí
                          </button>
                        }
                      </td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
          }
        }
      </section>
    </div>
  `,
  styles: [`
    :host {
      display: block;
      height: calc(100vh - 42px);
      overflow: hidden;
    }

    .exp-layout {
      display: flex;
      gap: 18px;
      padding: 20px 24px;
      max-width: 1440px;
      margin: 0 auto;
      height: 100%;
      box-sizing: border-box;
      overflow: hidden;
    }

    .exp-sidebar {
      width: 320px;
      flex-shrink: 0;
      display: flex;
      flex-direction: column;
      height: 100%;
      overflow: hidden;
    }

    .exp-side-top {
      padding: 15px;
      border-bottom: 1px solid var(--line);
    }

    .exp-list {
      flex: 1;
      overflow-y: auto;
      padding: 8px;
      display: flex;
      flex-direction: column;
      gap: 4px;
    }

    .exp-item {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 10px;
      border-radius: 10px;
      cursor: pointer;
    }
    .exp-item:hover { background: #F8FAFC; }
    .exp-item.active { background: #EFF6FF; border: 1px solid #BFDBFE; }

    .exp-avatar {
      width: 34px;
      height: 34px;
      border-radius: 9px;
      background: linear-gradient(135deg, #0E2A4F 0%, #035CB1 100%);
      color: #FFFFFF;
      font-size: 11px;
      font-weight: 800;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }

    .exp-main {
      flex: 1;
      min-width: 0;
      height: 100%;
      overflow-y: auto;
      padding-right: 6px;
      padding-bottom: 24px;
      display: block;
      scroll-behavior: smooth;
    }

    .exp-main > .card {
      margin-bottom: 16px;
    }

    .exp-main > .card:last-child {
      margin-bottom: 0;
    }

    .hero-patient {
      padding: 20px 24px;
    }

    .hero-top {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 14px;
      flex-wrap: wrap;
    }

    .hero-avatar {
      width: 48px;
      height: 48px;
      border-radius: 14px;
      background: linear-gradient(135deg, #035CB1 0%, #0E2A4F 100%);
      color: #FFFFFF;
      font-size: 16px;
      font-weight: 800;
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 6px 16px rgba(3, 92, 177, .28);
    }

    .eyebrow-tag {
      font-size: 10px;
      font-weight: 800;
      letter-spacing: .08em;
      color: var(--blue);
    }

    .hero-meta-grid {
      display: grid;
      grid-template-columns: repeat(5, 1fr);
      gap: 12px;
      margin-top: 18px;
      padding-top: 16px;
      border-top: 1px solid var(--line);
    }

    .meta-box {
      background: #F8FAFC;
      padding: 10px 12px;
      border-radius: 10px;
      border: 1px solid #EEF2F6;
      display: flex;
      flex-direction: column;
      gap: 3px;
    }

    .section-bar {
      padding: 14px 18px;
      border-bottom: 1px solid var(--line);
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    .move-here-btn {
      border: 1px solid #BBD4F2;
      background: var(--blue-soft);
      color: var(--blue);
      font: 700 11px var(--font);
      padding: 4px 10px;
      border-radius: 7px;
      cursor: pointer;
    }
    .move-here-btn:hover {
      background: var(--blue);
      color: #FFFFFF;
    }
  `]
})
export class PacientesComponent implements OnInit {
  readonly crm = inject(CrmService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  readonly contactos = signal<ContactoDto[]>([]);
  readonly ficha = signal<FichaContacto | null>(null);
  readonly citas = signal<CitaClinica[]>([]);
  readonly avisoCitas = signal<string | null>(null);
  readonly embudos = signal<EmbudoDto[]>([]);
  terminoBusqueda = '';

  constructor() {
    effect(() => {
      if (this.crm.versionDatos() > 0) this.buscar();
    });
  }

  ngOnInit() {
    this.crm.obtenerEmbudos().subscribe(e => this.embudos.set(e));
    this.route.queryParamMap.subscribe(qp => {
      const q = qp.get('q');
      if (q !== null) {
        this.terminoBusqueda = q;
        this.buscar();
      }
    });
    this.buscar();
    this.route.paramMap.subscribe(params => {
      const id = params.get('id');
      if (id) this.cargarFicha(id);
    });
  }

  buscar() {
    this.crm.buscarContactos(this.terminoBusqueda).subscribe(lista => {
      this.contactos.set(lista);
      if (!this.ficha() && lista.length > 0 && !this.route.snapshot.paramMap.get('id')) {
        this.cargarFicha(lista[0].id);
      }
    });
  }

  seleccionarPaciente(id: string) {
    this.router.navigate(['/pacientes', id]);
  }

  cargarFicha(id: string) {
    this.crm.obtenerFichaContacto(id).subscribe(f => this.ficha.set(f));
    this.crm.obtenerCitasContacto(id).subscribe({
      next: r => {
        this.citas.set(r.citas ?? []);
        this.avisoCitas.set(r.aviso);
      },
      error: () => {
        this.citas.set([]);
        this.avisoCitas.set(null);
      }
    });
  }

  etapasDeEmbudo(embudoId: string) {
    return this.embudos().find(e => e.id === embudoId)?.etapas ?? [];
  }

  estadoEtapa(ordenEtapa: number, caso: CasoDto): 'Completada' | 'En curso' | 'Pendiente' {
    const etapas = this.etapasDeEmbudo(caso.embudoId);
    const actual = etapas.find(e => e.id === caso.etapaId)?.orden ?? 1;
    if (caso.estado === 'ganado' || ordenEtapa < actual) return 'Completada';
    if (ordenEtapa === actual) return 'En curso';
    return 'Pendiente';
  }

  moverEtapa(caso: CasoDto, etapaId: string) {
    this.crm.cambiarEtapa(caso.id, etapaId).subscribe(() => {
      if (this.ficha()) this.cargarFicha(this.ficha()!.contacto.id);
    });
  }

  abrirNuevoCasoPaciente(contactoId: string) {
    this.crm.modalNuevoCaso.set({ abierto: true, contactoId });
  }

  iniciales(nombre: string): string {
    return nombre.split(' ').filter(Boolean).slice(0, 2).map(p => p[0].toUpperCase()).join('');
  }

  formatearCanal(origen: string | null): string {
    switch (origen) {
      case 'whatsapp': return 'WhatsApp';
      case 'instagram': return 'Instagram';
      case 'pbx': return 'PBX';
      default: return 'WhatsApp';
    }
  }
}
